import ObservabilityConsole from "../components/admin/ObservabilityConsole";
import OperationsCaseConsole from "../components/admin/OperationsCaseConsole";
import React, { useCallback, useEffect, useState } from "react";
import {
  INCIDENT_SEVERITIES,
  INCIDENT_STATUSES,
  OPERATIONAL_CATEGORIES,
  OPERATIONAL_QUEUE_STATUSES,
  OPERATIONAL_SOURCES,
  OPERATIONS_CONTRACT_VERSION,
  OPERATIONS_GUARDRAILS,
  type OperationalCategory,
  type OperationsRepository,
} from "../lib/forfeoOperationsContract";
import { fetchOperationsReadModel, type OperationsReadModel } from "../lib/operationsReadModel";

const categoryLabels:
  Record<OperationalCategory, string> = {
  incidents: "Incidents",
  errors: "Erreurs et alertes",
  queues: "Files opérationnelles",
  refunds: "Remboursements assistés",
  disputes: "Litiges",
  internal_notes: "Notes internes",
  recovery: "Procédures de reprise",
};

const repositoryLabels:
  Record<OperationsRepository, string> = {
  EXPLORE: "Explore",
  GIFTSHOP: "Giftshop",
};

export default function AdminOperations() {
  const [readModel, setReadModel] =
    useState<OperationsReadModel | null>(null);
  const [readModelError, setReadModelError] =
    useState<string | null>(null);
  const [isLoading, setIsLoading] =
    useState(false);

  const refreshReadModel = useCallback(async () => {
    setIsLoading(true);
    setReadModelError(null);

    try {
      const result =
        await fetchOperationsReadModel(24);
      setReadModel(result);
    } catch (error) {
      setReadModel(null);
      setReadModelError(
        error instanceof Error
          ? error.message
          : "operations_read_model_unavailable",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshReadModel();
  }, [refreshReadModel]);

  return (
    <main className="min-h-screen bg-slate-950 px-4 pb-16 pt-28 text-slate-100 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-7xl">
        <header className="mb-8 rounded-3xl border border-slate-800 bg-slate-900/80 p-5 shadow-2xl sm:p-7">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
                Lot 4C · Contrat {OPERATIONS_CONTRACT_VERSION}
              </p>

              <h1 className="text-3xl font-black tracking-tight text-white sm:text-4xl">
                Opérations et support
              </h1>

              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300 sm:text-base">
                Vue centralisée des sources opérationnelles
                existantes. Cette fondation demeure en lecture
                seule jusqu’au branchement serveur sécurisé du
                prochain sous-lot.
              </p>
            </div>

            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3">
              <p className="text-xs font-black uppercase tracking-wider text-emerald-300">
                Mode sécurisé
              </p>
              <p className="mt-1 text-sm font-semibold text-emerald-100">
                Lecture seule · aucune mutation financière
              </p>
            </div>
          </div>
        </header>

        <section
          aria-labelledby="operations-live-status"
          className="mb-8 rounded-3xl border border-slate-800 bg-slate-900/70 p-5 sm:p-6"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2
                id="operations-live-status"
                className="text-xl font-black text-white"
              >
                État opérationnel local
              </h2>
              <p className="mt-1 text-sm text-slate-400">
                Agrégats Explore des dernières 24 heures.
                Aucune ligne financière n’est retournée.
              </p>
            </div>

            <button
              type="button"
              onClick={() => void refreshReadModel()}
              disabled={isLoading}
              className="min-h-11 rounded-xl bg-cyan-400 px-4 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoading ? "Actualisation…" : "Actualiser"}
            </button>
          </div>

          {readModelError ? (
            <div
              role="status"
              className="mt-5 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4"
            >
              <p className="font-bold text-amber-200">
                Modèle serveur indisponible
              </p>
              <p className="mt-1 text-sm text-amber-100/80">
                La fonction demeure locale jusqu’à son
                déploiement autorisé. Code : {readModelError}
              </p>
            </div>
          ) : readModel ? (
            <>
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <MetricCard
                  label="Événements"
                  value={readModel.totals.events}
                />
                <MetricCard
                  label="Sources prêtes"
                  value={readModel.totals.ready_sources}
                />
                <MetricCard
                  label="Sources indisponibles"
                  value={
                    readModel.totals
                      .unavailable_sources
                  }
                />
                <MetricCard
                  label="Fenêtre"
                  value={readModel.window_hours}
                  suffix=" h"
                />
              </div>

              <ul className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-2">
                {readModel.sources.map(source => (
                  <li
                    key={source.id}
                    className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <code className="break-all text-xs text-slate-300">
                        {source.source_name}
                      </code>

                      <span
                        className={
                          source.status === "ready"
                            ? "rounded-full bg-emerald-400/10 px-2.5 py-1 text-xs font-black text-emerald-300"
                            : "rounded-full bg-amber-400/10 px-2.5 py-1 text-xs font-black text-amber-300"
                        }
                      >
                        {source.status}
                      </span>
                    </div>

                    <p className="mt-3 text-2xl font-black text-white">
                      {source.total ?? "—"}
                    </p>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p
              role="status"
              className="mt-5 text-sm text-slate-400"
            >
              Chargement du modèle de lecture…
            </p>
          )}
        </section>

        <section
          aria-labelledby="operations-guardrails"
          className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4"
        >
          <h2 id="operations-guardrails" className="sr-only">
            Règles opérationnelles
          </h2>

          <GuardrailCard
            label="Autorité remboursement"
            value={OPERATIONS_GUARDRAILS.refundAuthority}
          />
          <GuardrailCard
            label="Mutation navigateur"
            value={
              OPERATIONS_GUARDRAILS
                .financialMutationFromBrowser
            }
          />
          <GuardrailCard
            label="Échange interprojets"
            value={
              OPERATIONS_GUARDRAILS
                .crossProjectMutation
            }
          />
          <GuardrailCard
            label="Déploiement distant"
            value={
              OPERATIONS_GUARDRAILS.remoteDeployment
            }
          />
        </section>

        <section
          aria-labelledby="operations-lifecycle"
          className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-3"
        >
          <h2 id="operations-lifecycle" className="sr-only">
            Contrats de cycle de vie
          </h2>

          <LifecycleCard
            title="Sévérités"
            values={INCIDENT_SEVERITIES}
          />
          <LifecycleCard
            title="États d’incident"
            values={INCIDENT_STATUSES}
          />
          <LifecycleCard
            title="États de file"
            values={OPERATIONAL_QUEUE_STATUSES}
          />
        </section>

        <section aria-labelledby="operations-sources">
          <div className="mb-5">
            <h2
              id="operations-sources"
              className="text-2xl font-black text-white"
            >
              Sources opérationnelles
            </h2>
            <p className="mt-2 text-sm text-slate-400">
              Les compteurs en direct et les actions assistées
              seront branchés côté serveur dans C3.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            {OPERATIONAL_CATEGORIES.map(category => {
              const sources = OPERATIONAL_SOURCES.filter(
                source => source.category === category,
              );

              return (
                <article
                  key={category}
                  className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5 sm:p-6"
                >
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-lg font-black text-white">
                      {categoryLabels[category]}
                    </h3>

                    <span className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-xs font-bold text-slate-300">
                      {sources.length} source
                      {sources.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  {sources.length === 0 ? (
                    <p className="rounded-2xl border border-dashed border-slate-700 p-4 text-sm text-slate-400">
                      Source canonique à définir et brancher
                      dans C3.
                    </p>
                  ) : (
                    <ul className="space-y-3">
                      {sources.map(source => (
                        <li
                          key={source.id}
                          className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full bg-cyan-400/10 px-2.5 py-1 text-xs font-black text-cyan-300">
                              {
                                repositoryLabels[
                                  source.repository
                                ]
                              }
                            </span>

                            <code className="break-all text-xs text-slate-300">
                              {source.sourceName}
                            </code>
                          </div>

                          <p className="mt-3 text-sm font-bold text-white">
                            {source.owner}
                          </p>
                          <p className="mt-1 text-sm leading-6 text-slate-400">
                            {source.description}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      </div>
    
      <ObservabilityConsole />
      <OperationsCaseConsole />
</main>
  );
}

function MetricCard({
  label,
  value,
  suffix = "",
}: {
  label: string;
  value: number;
  suffix?: string;
}) {
  return (
    <article className="rounded-2xl border border-slate-800 bg-slate-950 p-4">
      <p className="text-xs font-black uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <p className="mt-2 text-2xl font-black text-white">
        {value}{suffix}
      </p>
    </article>
  );
}

function GuardrailCard({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <article className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <p className="text-xs font-black uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <p className="mt-2 break-words text-sm font-bold text-white">
        {value.replaceAll("_", " ")}
      </p>
    </article>
  );
}

function LifecycleCard({
  title,
  values,
}: {
  title: string;
  values: readonly string[];
}) {
  return (
    <article className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
      <h3 className="font-black text-white">{title}</h3>

      <div className="mt-4 flex flex-wrap gap-2">
        {values.map(value => (
          <span
            key={value}
            className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs font-bold text-slate-300"
          >
            {value}
          </span>
        ))}
      </div>
    </article>
  );
}
